import { useMutation, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import {
  buildJobChecklistItems,
  progressByJob,
  type ChecklistProgress,
  type ChecklistTemplateItem,
  type JobChecklistItem,
} from '@/lib/checklists';

// Checklist templates and job checklists (migration
// 20261102005644_job_checklists_photos_reports.sql). The generated Supabase
// types don't include the new tables/RPCs yet.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const db = supabase as any;

export interface ChecklistTemplate {
  id: string;
  organization_id: string;
  name: string;
  description: string | null;
  created_at: string;
  updated_at: string;
  items: ChecklistTemplateItem[];
}

export interface TemplateDraft {
  id?: string;
  name: string;
  description?: string | null;
  items: { id?: string; label: string; room: string | null }[];
}

const JOB_ITEM_COLUMNS =
  'id, job_id, template_item_id, label, room, sort_order, done, done_by, done_by_name, done_at, note';

export const checklistTemplatesKey = (orgId: string | null | undefined) => ['checklist-templates', orgId] as const;
export const jobChecklistKey = (jobId: string | null | undefined) => ['job-checklist', jobId] as const;

export function invalidateJobChecklists(queryClient: QueryClient, jobId?: string | null) {
  if (jobId) queryClient.invalidateQueries({ queryKey: jobChecklistKey(jobId) });
  queryClient.invalidateQueries({ queryKey: ['job-checklist-progress'] });
}

/** Every checklist template for the business, with its items in order. */
export function useChecklistTemplates(orgId: string | null | undefined) {
  return useQuery({
    queryKey: checklistTemplatesKey(orgId),
    enabled: Boolean(orgId),
    queryFn: async (): Promise<ChecklistTemplate[]> => {
      const { data, error } = await db
        .from('checklist_templates')
        .select('id, organization_id, name, description, created_at, updated_at, checklist_template_items(id, label, room, sort_order, created_at)')
        .eq('organization_id', orgId)
        .order('name');
      if (error) throw error;
      return ((data ?? []) as Array<ChecklistTemplate & { checklist_template_items?: ChecklistTemplateItem[] }>).map(
        ({ checklist_template_items, ...t }) => ({
          ...t,
          items: [...(checklist_template_items ?? [])].sort((a, b) => a.sort_order - b.sort_order),
        }),
      );
    },
  });
}

/** Create or update a template and replace its items with the draft's, in order. */
export function useSaveChecklistTemplate(orgId: string | null | undefined) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (draft: TemplateDraft): Promise<string> => {
      if (!orgId) throw new Error('No business selected');
      let templateId = draft.id;
      const fields = { name: draft.name.trim(), description: draft.description?.trim() || null };
      if (templateId) {
        const { error } = await db.from('checklist_templates').update(fields).eq('id', templateId).eq('organization_id', orgId);
        if (error) throw error;
      } else {
        const { data, error } = await db
          .from('checklist_templates')
          .insert({ ...fields, organization_id: orgId })
          .select('id')
          .single();
        if (error) throw error;
        templateId = data.id as string;
      }

      const items = draft.items
        .map((item) => ({ ...item, label: item.label.trim(), room: item.room?.trim() || null }))
        .filter((item) => item.label);
      const keepIds = items.map((item) => item.id).filter((id): id is string => Boolean(id));

      // Remove items the owner deleted, then write the rest in their new order.
      let remove = db.from('checklist_template_items').delete().eq('template_id', templateId);
      if (keepIds.length > 0) remove = remove.not('id', 'in', `(${keepIds.join(',')})`);
      const { error: removeError } = await remove;
      if (removeError) throw removeError;

      const existing = items
        .map((item, index) => ({ item, index }))
        .filter(({ item }) => item.id);
      const added = items
        .map((item, index) => ({ item, index }))
        .filter(({ item }) => !item.id);

      if (existing.length > 0) {
        const { error } = await db.from('checklist_template_items').upsert(
          existing.map(({ item, index }) => ({
            id: item.id,
            template_id: templateId,
            organization_id: orgId,
            label: item.label,
            room: item.room,
            sort_order: index,
          })),
        );
        if (error) throw error;
      }
      if (added.length > 0) {
        const { error } = await db.from('checklist_template_items').insert(
          added.map(({ item, index }) => ({
            template_id: templateId,
            organization_id: orgId,
            label: item.label,
            room: item.room,
            sort_order: index,
          })),
        );
        if (error) throw error;
      }
      return templateId!;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: checklistTemplatesKey(orgId) }),
  });
}

export function useDeleteChecklistTemplate(orgId: string | null | undefined) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (templateId: string) => {
      const { error } = await db.from('checklist_templates').delete().eq('id', templateId).eq('organization_id', orgId);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: checklistTemplatesKey(orgId) });
      queryClient.invalidateQueries({ queryKey: ['service-checklist-template'] });
    },
  });
}

/** Which services use which template: { templateId: [service names] }. */
export function useServicesByChecklistTemplate(orgId: string | null | undefined) {
  return useQuery({
    queryKey: ['services-by-checklist', orgId],
    enabled: Boolean(orgId),
    queryFn: async (): Promise<Record<string, string[]>> => {
      const { data, error } = await db
        .from('services')
        .select('id, name, checklist_template_id')
        .eq('organization_id', orgId)
        .not('checklist_template_id', 'is', null);
      if (error) return {};
      const out: Record<string, string[]> = {};
      for (const row of (data ?? []) as { name: string; checklist_template_id: string }[]) {
        (out[row.checklist_template_id] ??= []).push(row.name);
      }
      return out;
    },
  });
}

// --- Services: default template ---------------------------------------------

export function useServiceChecklistTemplate(serviceId: string | null | undefined) {
  return useQuery({
    queryKey: ['service-checklist-template', serviceId],
    enabled: Boolean(serviceId),
    queryFn: async (): Promise<string | null> => {
      const { data, error } = await db.from('services').select('checklist_template_id').eq('id', serviceId).maybeSingle();
      if (error) return null;
      return (data?.checklist_template_id as string | null) ?? null;
    },
  });
}

export async function setServiceChecklistTemplate(serviceId: string, templateId: string | null) {
  const { error } = await db.from('services').update({ checklist_template_id: templateId }).eq('id', serviceId);
  if (error) throw error;
}

// --- Job checklists -----------------------------------------------------------

export function useJobChecklist(jobId: string | null | undefined) {
  return useQuery({
    queryKey: jobChecklistKey(jobId),
    enabled: Boolean(jobId),
    queryFn: async (): Promise<JobChecklistItem[]> => {
      const { data, error } = await db
        .from('job_checklist_items')
        .select(JOB_ITEM_COLUMNS)
        .eq('job_id', jobId)
        .order('sort_order');
      if (error) throw error;
      return (data ?? []) as JobChecklistItem[];
    },
  });
}

/** Tick or untick an item (optimistic). Cleaners and managers. */
export function useToggleChecklistItem(jobId: string) {
  const queryClient = useQueryClient();
  const key = jobChecklistKey(jobId);
  return useMutation({
    mutationFn: async ({ itemId, done, note }: { itemId: string; done: boolean; note?: string | null }) => {
      const { data, error } = await db.rpc('set_job_checklist_item', {
        _item_id: itemId,
        _done: done,
        _note: note === undefined ? null : note ?? '',
      });
      if (error) throw error;
      return (Array.isArray(data) ? data[0] : data) as JobChecklistItem;
    },
    onMutate: async ({ itemId, done, note }) => {
      await queryClient.cancelQueries({ queryKey: key });
      const previous = queryClient.getQueryData<JobChecklistItem[]>(key);
      queryClient.setQueryData<JobChecklistItem[]>(key, (items) =>
        (items ?? []).map((item) =>
          item.id === itemId
            ? { ...item, done, ...(note !== undefined ? { note: note?.trim() || null } : {}) }
            : item,
        ),
      );
      return { previous };
    },
    onError: (_error, _vars, context) => {
      if (context?.previous) queryClient.setQueryData(key, context.previous);
    },
    onSuccess: (row) => {
      if (row?.id) {
        queryClient.setQueryData<JobChecklistItem[]>(key, (items) =>
          (items ?? []).map((item) => (item.id === row.id ? { ...item, ...row } : item)),
        );
      }
      queryClient.invalidateQueries({ queryKey: ['job-checklist-progress'] });
    },
  });
}

/** Owner picks a checklist for a job: copy the template's items onto the end of the list. */
export async function applyTemplateToJob(jobId: string, template: Pick<ChecklistTemplate, 'items'>, existingCount = 0) {
  const rows = buildJobChecklistItems(jobId, template.items, existingCount);
  if (rows.length === 0) return 0;
  const { error } = await db.from('job_checklist_items').insert(rows);
  if (error) throw error;
  return rows.length;
}

export async function addJobChecklistItem(jobId: string, label: string, room: string | null, sortOrder: number) {
  const { error } = await db
    .from('job_checklist_items')
    .insert({ job_id: jobId, label: label.trim(), room: room?.trim() || null, sort_order: sortOrder });
  if (error) throw error;
}

export async function deleteJobChecklistItem(itemId: string) {
  const { error } = await db.from('job_checklist_items').delete().eq('id', itemId);
  if (error) throw error;
}

export async function clearJobChecklist(jobId: string) {
  const { error } = await db.from('job_checklist_items').delete().eq('job_id', jobId);
  if (error) throw error;
}

/** Checklist progress ("12/15") for the jobs on screen, in one query. */
export function useChecklistProgress(jobIds: string[]) {
  const ids = [...new Set(jobIds)].sort();
  return useQuery({
    queryKey: ['job-checklist-progress', ids],
    enabled: ids.length > 0,
    staleTime: 15_000,
    queryFn: async (): Promise<Map<string, ChecklistProgress>> => {
      const { data, error } = await db.from('job_checklist_items').select('job_id, done').in('job_id', ids);
      // Progress is a nice-to-have on cards: never break the calendar over it.
      if (error) return new Map();
      return progressByJob(data as { job_id: string; done: boolean }[]);
    },
  });
}
